import dayjs from "dayjs";
import _ from "lodash";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { MATERIAL_OPTIONS } from "../../../utils/constants";

import Button from "../../themed/button";
import Conditional from "../../common/conditional";
import DatePickerInput from "../../themed/date-picker";
import Input from "../../themed/input/input";
import Select from "../../themed/select/select";

const HardnessEquipmentSchema = z.object({
    id: z.number().positive().optional(),
    equipmentName: z.string().min(3, "Test Location is required"),
    modal: z.string().min(3, "Modal is required."),
    equipmentSerialNumber: z.string().min(3, "Serial Number is required"),
    calibrationDueDate: z.date().min(dayjs().toDate(), 'Calibration due date must be in the future'),
    calibrationSerialNumber: z.string().min(3, "Serial Number is required"),
    material: z.string().min(3, 'Material is required'),
    surfaceCondition: z.string().min(3, 'Surface Condition is required'),
    stageOfInspection: z.string().min(3, 'Stage of Inspection is required'),
    testMethod: z.string().min(3, 'Test Method is required'),
    loadApplied: z.coerce.number().min(0, 'Load applied cannot be less than 0'),
});

export type HardnessEquipment = z.infer<typeof HardnessEquipmentSchema>;

type Props = {
    onSave: (info: HardnessEquipment) => void;
    onDelete?: () => void;
    initialItem?: HardnessEquipment;
};

const HardnessEquipmentForm: React.FC<Props> = ({ initialItem, onDelete, onSave }) => {
    const form = useForm<HardnessEquipment>({
        resolver: zodResolver(HardnessEquipmentSchema),
        mode: 'all',
        defaultValues: initialItem,
    });

    const handleAddHardnessEquipment = (data: HardnessEquipment) => {
        onSave(data);
    };

    return (  
        <form onSubmit={form.handleSubmit(handleAddHardnessEquipment)}>
                <div className="container">
                    <div className="row">
                        <div className="col">
                            <Controller
                                control={form.control}
                                name='equipmentName'
                                render={({ field }) => (
                                    <Input
                                        type="text"
                                        label="Equipment Name"
                                        placeholder="Enter Equipment Name"
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.equipmentName ? form.formState.errors.equipmentName.message : ''}
                                    />
                                )}
                            />
                        </div>
                        
                        <div className="col">
                            <Controller
                                control={form.control}
                                name='modal'
                                render={({ field }) => (
                                    <Input
                                        type="text"
                                        label="Modal"
                                        placeholder="Enter Modal"
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.modal ? form.formState.errors.modal.message : ''}
                                    />
                                )}
                            />
                        </div>
                        
                        <div className="col">
                            <Controller
                                control={form.control}
                                name="equipmentSerialNumber"
                                render={({ field }) => (
                                    <Input
                                        type="text"
                                        label="Equipment Serial Number"
                                        placeholder="Enter Equipment Serial Number"
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.equipmentSerialNumber ? form.formState.errors.equipmentSerialNumber.message : ''}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    
                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            <Controller
                                control={form.control}
                                name="surfaceCondition"
                                render={({ field }) => (
                                    <Select
                                        options={SURFACE_CONDITIONS}
                                        label="Surface Condition"
                                        placeholder="Enter Surface Condition"
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.surfaceCondition ? form.formState.errors.surfaceCondition.message : ''}
                                    />
                                )}
                            />
                        </div>

                        <div className="col">
                            <Controller
                                control={form.control}
                                name="stageOfInspection"
                                render={({ field }) => (
                                    <Select
                                        options={STAGE_OF_INSPECTION}
                                        label="Stage of Inspection"
                                        placeholder="Enter Stage of Inspection"
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.stageOfInspection ? form.formState.errors.stageOfInspection.message : ''}
                                    />
                                )}
                            />
                        </div>
                        
                        <div className="col">
                            <Controller
                                control={form.control}
                                name="testMethod"
                                render={({ field }) => (
                                    <Input
                                        type="text"
                                        label="Test Method"
                                        placeholder="Enter Test Method"
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.testMethod ? form.formState.errors.testMethod.message : ''}
                                    />
                                )}
                            />
                        </div>
                        
                        <div className="col">
                            <Controller
                                control={form.control}
                                name="loadApplied"
                                render={({ field }) => (
                                    <Select
                                        options={_.range(50, 5001, 25).map((fill) => ({ id: fill, name: fill }))}
                                        label="Load Applied (grams)"
                                        placeholder="Enter Load Applied (grams)"
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.loadApplied ? form.formState.errors.loadApplied.message : ''}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    
                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            <Controller
                                control={form.control}
                                name="calibrationDueDate"
                                render={({ field }) => (
                                    <DatePickerInput
                                        label="Calibration Due Date"
                                        placeholderText="Select Calibration Due Date"
                                        minDate={dayjs().toDate()}
                                        date={field.value}
                                        error={form.formState.errors.calibrationDueDate ? form.formState.errors.calibrationDueDate.message : ''}
                                        onDateChange={field.onChange}
                                    />
                                )}
                            />
                        </div>

                        <div className="col">
                            <Controller
                                control={form.control}
                                name="calibrationSerialNumber"
                                render={({ field }) => (
                                    <Input
                                        type="text"
                                        label="Calibration Block Serial Number"
                                        placeholder="Enter Calibration Block Serial Number"
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.calibrationSerialNumber ? form.formState.errors.calibrationSerialNumber.message : ''}
                                    />
                                )}
                            />
                        </div>
                        
                        <div className="col">
                            <Controller
                                control={form.control}
                                name="material"
                                render={({ field }) => (
                                    <Select
                                        label="Material"
                                        placeholder="Select Material"
                                        options={MATERIAL_OPTIONS}
                                        value={field.value}
                                        onChange={field.onChange}
                                        name={field.name}
                                        error={form.formState.errors.material ? form.formState.errors.material.message : ''}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="divider my-4"></div>

                <div className="d-flex justify-content-end">
                    <Conditional isVisible={!!onDelete}>
                        <div className="me-3">
                            <Button
                                onClick={onDelete} 
                                icon="trash"
                                type="button"
                                label="Delete" 
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    </Conditional>

				    <Button
                        type="submit"
                        disabled={!form.formState.isValid}
                        label={form.formState.isSubmitting ? "Saving Information..." : "Save Information"}
                    />
                </div>
        </form>
    );
};

const SURFACE_CONDITIONS = [
    {
        id: 'Smooth',
        name: 'Smooth',
    },
    {
        id: 'As Welded',
        name: 'As Welded',
    },
    {
        id: 'Grinded',
        name: 'Grinded',
    },
    {
        id: 'Heat Affected',
        name: 'Heat Affected',
    },
    {
        id: 'Clean Prepared',
        name: 'Clean Prepared',
    },
]

const STAGE_OF_INSPECTION = [
    {
        id: 'In Service',
        name: 'In Service',
    },
    {
        id: 'Before Weld',
        name: 'Before Weld',
    },
    {
        id: 'After Weld',
        name: 'After Weld',
    },
];
 
export default HardnessEquipmentForm;